// T-074 data grid (design D8). The server renders every control as a link or a GET form, so a grid works without this
// file; here those links and the form fetch only the grid fragment (X-DS-Grid header) and swap it in place, the
// address follows (back/forward work), search is debounced, and preference forms post in the background.
// Pure helpers are exported for node --test (tools/ds-tests/grid.test.mjs).
import type { ActionAnswer } from './ds/flash.ts';
import { fetchFragment, remember, type HistoryMode } from './ds/fragment.ts';

/** How the grid says something: the design system's toast with its grid words ('error' = a failure). */
export type GridToast = (msg: string, type: string) => void;
/** What ds/index.ts hands the grid. */
export interface GridHost {
  toast?: GridToast;
  enhance?: (root: Element) => void;
  closeLayers?: () => void;
}
/** A debounced function, with a way to drop the call that is waiting. */
export type Debounced<A extends unknown[]> = ((...args: A) => void) & { cancel(): void };

export const HEADER = 'X-DS-Grid';

/** The query string of a grid form (and of the controls attached to it by form=), without empty values. */
export function formQuery(entries: Iterable<[string, FormDataEntryValue]>): string {
  const params = new URLSearchParams();
  for (const [name, value] of entries) {
    if (typeof value === 'string' && value.trim() !== '') params.append(name, value.trim());
  }
  const text = params.toString();
  return text ? '?' + text : '';
}

export function debounce<A extends unknown[]>(fn: (...args: A) => void, ms: number): Debounced<A> {
  let timer = 0;
  const wrapped = (...args: A): void => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
  (wrapped as Debounced<A>).cancel = () => clearTimeout(timer);
  return wrapped as Debounced<A>;
}

/** A plain left click without modifier keys (a ctrl/middle click still opens a new tab). */
export function isPlainClick(e: MouseEvent): boolean {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

/** The text of the selection counter. */
export function pickedText(n: number): string {
  return n === 0 ? 'موردی انتخاب نشده' : `${n} مورد انتخاب شد`;
}

// T-087: which ends of a scrolling tab bar hide tabs. RTL scrollLeft runs from 0 (start, right) to negative values, so
// only its size counts; «before» = tabs hidden past the start (right in RTL), «after» = past the end (left in RTL).
export function scrollEdges(
  scrollLeft: number,
  clientWidth: number,
  scrollWidth: number,
): { before: boolean; after: boolean } {
  const moved = Math.abs(scrollLeft);
  return { before: moved > 2, after: moved + clientWidth < scrollWidth - 2 };
}

function syncScopes(grid: Element | null | undefined): void {
  const bar = grid?.querySelector('.ds-dg-scopes');
  if (!bar) return;
  const sync = (): void => {
    const e = scrollEdges(bar.scrollLeft, bar.clientWidth, bar.scrollWidth);
    bar.classList.toggle('more-before', e.before);
    bar.classList.toggle('more-after', e.after);
  };
  const active = bar.querySelector('[aria-current="page"]');
  if (active && bar.scrollWidth > bar.clientWidth) active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  bar.addEventListener('scroll', sync, { passive: true });
  // T-160 (Sina 2026-10-14): a mouse wheel over the tabs moves them sideways (a desktop has no swipe); only while tabs are
  // hidden in the wheel's direction, so at either end the page scrolls as usual
  bar.addEventListener(
    'wheel',
    e => {
      const we = e as WheelEvent;
      if (Math.abs(we.deltaY) <= Math.abs(we.deltaX) || bar.scrollWidth <= bar.clientWidth) return;
      const rtl = getComputedStyle(bar).direction === 'rtl';
      const step = rtl ? -we.deltaY : we.deltaY;
      const before = bar.scrollLeft;
      bar.scrollLeft += step;
      if (bar.scrollLeft !== before) we.preventDefault();
    },
    { passive: false },
  );
  sync();
}

export function initGrids({ toast, enhance, closeLayers }: GridHost): void {
  const running = new WeakMap<HTMLElement, AbortController>();
  const grids = (): HTMLElement[] => [...document.querySelectorAll<HTMLElement>('[data-ds-datagrid]')];
  const gridOf = (el: Element): HTMLElement | null => el.closest('[data-ds-datagrid]');

  async function load(grid: HTMLElement | null, url: string, history: HistoryMode): Promise<void> {
    if (!grid) return;
    running.get(grid)?.abort();
    const ctrl = new AbortController();
    running.set(grid, ctrl);
    grid.setAttribute('aria-busy', 'true');
    const active = document.activeElement as HTMLInputElement | null;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- an event target can be the document or a text node, which have no closest/matches
    const searching = active?.matches?.('[data-ds-grid-search]') && grid.contains(active);
    const caret = searching ? active!.selectionStart : null;
    try {
      const { element: next, status } = await fetchFragment(
        url,
        HEADER,
        grid.dataset.dsDatagrid!,
        '[data-ds-datagrid]',
        ctrl.signal,
      );
      if (!next) {
        // signed out, a redirect, or an error page: let the browser show what the server answers
        if (status === 403) {
          toast?.('دسترسی به این فهرست را ندارید', 'error');
          grid.setAttribute('aria-busy', 'false');
          return;
        }
        window.location.assign(url);
        return;
      }
      closeLayers?.();
      // T-160: «فیلترها» stays open while the user sets one filter after another (each change reloads the grid)
      const moreOpen = grid.querySelector<HTMLDetailsElement>('[data-ds-grid-more]')?.open ?? false;
      const more = next.querySelector<HTMLDetailsElement>('[data-ds-grid-more]');
      if (more && moreOpen) more.open = true;
      grid.replaceWith(next);
      enhance?.(next);
      syncPicks(next);
      syncScopes(next);
      remember(history, { dsGrid: next.id }, url);
      if (searching) {
        const input = next.querySelector<HTMLInputElement>('[data-ds-grid-search]');
        if (input) {
          input.focus();
          if (caret !== null) input.setSelectionRange(caret, caret);
        }
      }
    } catch (err) {
      if ((err as Error | null)?.name === 'AbortError') return;
      grid.setAttribute('aria-busy', 'false');
      toast?.('بارگذاری فهرست انجام نشد؛ دوباره تلاش کنید', 'error');
    }
  }

  const urlOf = (form: HTMLFormElement): string =>
    (gridOf(form)?.dataset.path || form.getAttribute('action') || window.location.pathname) +
    formQuery(new FormData(form));

  // the handlers below start a load and do not wait for it (load reports its own failures)
  /* eslint-disable @typescript-eslint/no-floating-promises */

  // links: tabs, sort headers, pager, saved views, «clear»
  document.addEventListener('click', e => {
    const a = (e.target as Element).closest<HTMLAnchorElement>('a[data-ds-grid-link]');
    if (!a || !isPlainClick(e)) return;
    const grid = gridOf(a);
    if (!grid) return;
    e.preventDefault();
    a.closest('.ds-menu')?.setAttribute('hidden', '');
    load(grid, a.href, 'push');
  });

  // search: debounced, the address replaced while typing
  const typed = debounce((form: HTMLFormElement) => load(gridOf(form), urlOf(form), 'replace'), 350);
  document.addEventListener('input', e => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- an event target can be the document or a text node, which have no closest/matches
    const input = (e.target as Element).closest?.<HTMLInputElement>('[data-ds-grid-search]');
    if (input?.form) typed(input.form);
  });

  // filters and page size: at once
  document.addEventListener('change', e => {
    const el = e.target as HTMLInputElement;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- an event target can be the document or a text node, which have no closest/matches
    if (el.matches?.('[data-ds-grid-filter], [data-ds-grid-size]') && el.form) {
      typed.cancel();
      load(gridOf(el.form) || gridOf(el), urlOf(el.form), 'push');
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- an event target can be the document or a text node, which have no closest/matches
    if (el.matches?.('[data-ds-grid-pick], [data-ds-grid-all]')) {
      const grid = gridOf(el)!;
      if (el.matches('[data-ds-grid-all]'))
        grid.querySelectorAll<HTMLInputElement>('[data-ds-grid-pick]').forEach(c => {
          c.checked = el.checked;
        });
      syncPicks(grid);
    }
  });

  document.addEventListener('submit', async e => {
    const form = e.target as HTMLFormElement;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- an event target can be the document or a text node, which have no closest/matches
    if (form.matches?.('[data-ds-grid-form]')) {
      e.preventDefault();
      typed.cancel();
      load(gridOf(form), urlOf(form), 'push');
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- an event target can be the document or a text node, which have no closest/matches
    if (!form.matches?.('form[data-ds-grid-pref]')) return;
    // a preference (column, density, saved view): saved in the background, then the grid reloads at the same address
    e.preventDefault();
    const grid = gridOf(form);
    try {
      const res = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { [HEADER]: grid?.dataset.dsDatagrid || '1' },
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(String(res.status));
      form.closest('dialog')?.close();
      if (form.querySelector<HTMLInputElement>('input[name="op"]')?.value === 'view-add')
        toast?.('نما ذخیره شد', 'success');
      load(grid, window.location.href, null);
    } catch {
      toast?.('ذخیره‌ی تنظیمات فهرست انجام نشد', 'error');
    }
  });

  // T-127: a row action that changes data (a[data-method="post"] in a grid) runs in place: posted in the background with
  // X-DS-Action, the server answers { ok, messages } (ActionOutcomeFilter), the grid shows them and reloads at the same
  // address, so the tab, filters, page and search stay. Capture phase, so antiforgery.js never also posts it; a link still
  // waiting for its confirm dialog is left to ds.js, which clicks it again once confirmed.
  document.addEventListener(
    'click',
    async e => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- an event target can be the document or a text node, which have no closest/matches
      const link = (e.target as Element).closest?.<HTMLAnchorElement>('[data-ds-datagrid] a[data-method="post"]');
      if (!link || (link.hasAttribute('data-ds-confirm') && !link.dataset.dsConfirmed)) return;
      if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey) return;
      e.preventDefault();
      e.stopPropagation();
      const grid = gridOf(link);
      const label = (link.getAttribute('aria-label') || link.textContent || '').trim();
      const token = document.querySelector<HTMLMetaElement>('meta[name="ds-csrf"]')?.content || '';
      const body = new FormData();
      body.append('__RequestVerificationToken', token);
      closeLayers?.();
      grid?.setAttribute('aria-busy', 'true');
      let res: Response;
      try {
        res = await fetch(link.href, {
          method: 'POST',
          body,
          headers: { 'X-DS-Action': '1', Accept: 'application/json' },
          credentials: 'same-origin',
        });
      } catch {
        grid?.setAttribute('aria-busy', 'false');
        toast?.('ارتباط با سامانه برقرار نشد؛ دوباره امتحان کنید', 'danger');
        return;
      }
      const answer = (
        (res.headers.get('content-type') || '').includes('json') ? await res.json().catch(() => null) : null
      ) as ActionAnswer | null;
      if (!answer) {
        // refused before the action ran (an expired page token, a permission) or signed out: say so, then reload, which also
        // brings a fresh token; the message survives the reload (ds.flash)
        if (!res.redirected && res.status >= 400) {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- ds.min.js may not have run yet
          window.ds?.flash(
            res.status === 403 ? 'اجازه‌ی این کار را ندارید.' : 'این کار انجام نشد؛ صفحه تازه شد، دوباره امتحان کنید.',
            { type: 'danger' },
          );
        }
        window.location.reload();
        return;
      }
      if (answer.ok) {
        if (answer.messages?.length) answer.messages.forEach(m => toast?.(m.text, m.type || 'success'));
        else toast?.(label ? `«${label}» انجام شد` : 'انجام شد', 'success');
      } else {
        toast?.(answer.message || 'این کار انجام نشد', 'danger');
      }
      load(grid, window.location.href, null);
    },
    true,
  );

  // «/» jumps to the grid's search box (Draft 11), unless the user is already typing somewhere
  document.addEventListener('keydown', e => {
    if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
    const t = e.target as Element | null;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- an event target can be the document or a text node, which have no closest/matches
    if (t?.closest?.('input, textarea, select, [contenteditable], dialog[open]')) return;
    const box = document.querySelector<HTMLInputElement>('[data-ds-datagrid] [data-ds-grid-search]');
    if (!box) return;
    e.preventDefault();
    box.focus();
    box.select();
  });

  window.addEventListener('popstate', () => {
    grids()
      .filter(g => g.dataset.path === window.location.pathname)
      .forEach(g => load(g, window.location.href, null));
  });
  /* eslint-enable @typescript-eslint/no-floating-promises */

  grids().forEach(syncPicks);
  grids().forEach(syncScopes);
  window.addEventListener(
    'resize',
    debounce(() => grids().forEach(g => g.querySelector('.ds-dg-scopes')?.dispatchEvent(new Event('scroll'))), 150),
  );

  // the print export prints itself once the page is drawn
  if (document.querySelector('[data-ds-autoprint]')) setTimeout(() => window.print(), 400);
}

/** The bulk bar shows only with a selection, and says how many rows are picked. */
function syncPicks(grid: Element | null | undefined): void {
  const bar = grid?.querySelector<HTMLElement>('[data-ds-grid-bulk]');
  if (!bar) return;
  const picks = [...grid!.querySelectorAll<HTMLInputElement>('[data-ds-grid-pick]')];
  const n = picks.filter(c => c.checked).length;
  bar.hidden = n === 0;
  const count = bar.querySelector('[data-ds-grid-count]');
  if (count) count.textContent = pickedText(n);
  const all = grid!.querySelector<HTMLInputElement>('[data-ds-grid-all]');
  if (all) {
    all.checked = n > 0 && n === picks.length;
    all.indeterminate = n > 0 && n < picks.length;
  }
}
